const cleanFilePart = (value) => String(value || "measurement-documentation")
  .trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`

export const measurementRows = (mapping, revision) => {
  const spec = revision?.spec || {}
  return [
    ...(spec.configurations || []).map((item) => ({
      kind: "GA4 configuration",
      reportingLabel: item.reporting_label || item.name,
      technicalName: item.measurement_id || mapping.ga4_measurement_id,
      trigger: (item.triggers || []).map((trigger) => trigger.name).join("; "),
      parameters: "",
      purpose: item.purpose || "",
      notes: item.notes || "",
      management: item.adopted ? "Adopted" : "Documented",
    })),
    ...(spec.events || []).map((item) => ({
      kind: "GA4 event",
      reportingLabel: item.reporting_label || item.event_name,
      technicalName: item.event_name,
      trigger: (item.triggers || []).map((trigger) => trigger.name).join("; "),
      parameters: Object.entries(item.parameter_mappings || {})
        .map(([name, value]) => `${name}=${typeof value === "string" ? value : JSON.stringify(value)}`).join("; "),
      purpose: item.purpose || "",
      notes: item.notes || "",
      management: item.resource_id ? "Imported" : "App managed",
    })),
    ...(spec.unsupported || []).map((item) => ({
      kind: "Read-only GTM resource",
      reportingLabel: item.name,
      technicalName: item.tag_type,
      trigger: "",
      parameters: "",
      purpose: "",
      notes: item.reason || "Unsupported resource type",
      management: "Read only",
    })),
  ]
}

export const buildMeasurementCsv = (mapping, revision) => {
  const identity = [
    ["Account domain", mapping.normalized_domain],
    ["GTM container", `${mapping.gtm_container_name} (${mapping.gtm_public_id})`],
    ["GA4 property", mapping.ga4_property_name],
    ["GA4 web stream", `${mapping.ga4_stream_name} (${mapping.ga4_measurement_id})`],
    ["Documentation revision", revision?.version || ""],
    ["Documentation status", revision?.status || "draft"],
  ]
  const headers = ["Type", "Reporting label", "Technical name", "Trigger", "Parameters", "Purpose", "Notes", "Management"]
  const rows = measurementRows(mapping, revision)
  return "\uFEFF" + [
    ...identity.map(([name, value]) => `${csvCell(name)},${csvCell(value)}`),
    "",
    headers.map(csvCell).join(","),
    ...rows.map((row) => [
      row.kind, row.reportingLabel, row.technicalName, row.trigger,
      row.parameters, row.purpose, row.notes, row.management,
    ].map(csvCell).join(",")),
  ].join("\r\n")
}

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export const exportMeasurementCsv = (mapping, revision) => {
  downloadBlob(
    new Blob([buildMeasurementCsv(mapping, revision)], { type: "text/csv;charset=utf-8" }),
    `${cleanFilePart(mapping.normalized_domain)}-measurement-documentation-v${revision.version}.csv`,
  )
}

export const exportMeasurementPdf = async (mapping, revision, accountName) => {
  const [{ jsPDF }, { autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ])
  const document = new jsPDF({ unit: "pt", format: "letter" })
  const isDraft = revision.status === "draft"
  document.setFont("helvetica", "bold")
  document.setFontSize(18)
  document.text("Measurement implementation documentation", 40, 48)
  if (isDraft) {
    document.setTextColor(180, 70, 40)
    document.setFontSize(12)
    document.text("DRAFT — NOT APPROVED FOR CLIENT REPORTING", 40, 69)
    document.setTextColor(30, 30, 30)
  }
  document.setFont("helvetica", "normal")
  document.setFontSize(10)
  const details = [
    ["Account", accountName || ""],
    ["Domain", mapping.normalized_domain],
    ["GTM", `${mapping.gtm_container_name} (${mapping.gtm_public_id})`],
    ["GA4 property", mapping.ga4_property_name],
    ["GA4 web stream", `${mapping.ga4_stream_name} (${mapping.ga4_measurement_id})`],
    ["Revision", `v${revision.version} · ${revision.status}`],
  ]
  autoTable(document, {
    startY: isDraft ? 82 : 70,
    body: details,
    theme: "plain",
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 90 } },
  })
  const summaryY = document.lastAutoTable.finalY + 16
  const summary = revision.spec?.summary || revision.spec?.client_notes || ""
  let summaryLines = []
  if (summary) {
    document.setFont("helvetica", "bold")
    document.text("Implementation summary", 40, summaryY)
    document.setFont("helvetica", "normal")
    summaryLines = document.splitTextToSize(summary, 530)
    document.text(summaryLines, 40, summaryY + 14)
  }
  const tableY = summary ? summaryY + 26 + (summaryLines.length * 11) : summaryY
  autoTable(document, {
    startY: tableY,
    head: [["Type", "Reporting label", "Technical name", "Trigger", "Parameters", "Purpose / notes"]],
    body: measurementRows(mapping, revision).map((row) => [
      row.kind, row.reportingLabel, row.technicalName, row.trigger,
      row.parameters, [row.purpose, row.notes].filter(Boolean).join(" — "),
    ]),
    styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
    headStyles: { fillColor: [35, 85, 110], textColor: 255 },
    alternateRowStyles: { fillColor: [244, 247, 249] },
    margin: { left: 32, right: 32 },
  })
  document.save(`${cleanFilePart(mapping.normalized_domain)}-measurement-documentation-v${revision.version}.pdf`)
}
